import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { NotificationProvider } from "@/components/NotificationProvider";
import { LanguageProvider } from "@/components/LanguageProvider";

// Pages
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { AviatorPage } from "@/pages/AviatorPage";
import { CoinBalancePage } from "@/pages/CoinBalancePage";
import { DicePage } from "@/pages/DicePage";
import { SlotsPage } from "@/pages/SlotsPage";
import { MinesPage } from "@/pages/MinesPage";
import { PlinkoPage } from "@/pages/PlinkoPage";
import { WheelPage } from "@/pages/WheelPage";
import { CoinLemonPage } from "@/pages/CoinLemonPage";
import { CrashPage } from "@/pages/CrashPage";
import { LimboPage } from "@/pages/LimboPage";
import { KenoPage } from "@/pages/KenoPage";
import { NewsBettingPage } from "@/pages/NewsBettingPage";
import { AICreatorPage } from "@/pages/AICreatorPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { LeaderboardPage } from "@/pages/LeaderboardPage";
import { VIPPage } from "@/pages/VIPPage";
import { ReferralPage } from "@/pages/ReferralPage";
import { TournamentsPage } from "@/pages/TournamentsPage";
import { SupportPage } from "@/pages/SupportPage";
import { PromosPage } from "@/pages/PromosPage";
import { ChatRoomPage } from "@/pages/ChatRoomPage";
import { WithdrawPage } from "@/pages/WithdrawPage";
import { ResponsibleGamingPage } from "@/pages/ResponsibleGamingPage";
import { AdminPage } from "@/pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { 
        withCredentials: true,
        headers 
      });
      setUser(response.data);
    } catch (e) {
      setUser(null);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    if (authToken) {
      localStorage.setItem("token", authToken);
      setToken(authToken);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
    localStorage.removeItem("token");
    setToken(null);
  };

  const refreshBalance = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/wallet/balance`, { 
        withCredentials: true,
        headers 
      });
      setUser(prev => prev ? { ...prev, balance: response.data.balance } : null);
    } catch (e) {
      console.error("Balance refresh error:", e);
    }
  };

  return (
    <UserContext.Provider value={{ user, loading, login, logout, token, refreshBalance, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const UserContext = require('react').createContext(null);

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = require('react').useContext(UserContext);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/google/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        login(response.data.user, null);
        navigate('/dashboard', { state: { user: response.data.user }, replace: true });
      } catch (e) {
        console.error("Auth callback error:", e);
        navigate('/login');
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F0F10]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#00E701] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#A1A1AA]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = require('react').useContext(UserContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F0F10]">
        <div className="w-12 h-12 border-4 border-[#00E701] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/games/aviator" element={<ProtectedRoute><AviatorPage /></ProtectedRoute>} />
      <Route path="/games/coinbalance" element={<ProtectedRoute><CoinBalancePage /></ProtectedRoute>} />
      <Route path="/games/dice" element={<ProtectedRoute><DicePage /></ProtectedRoute>} />
      <Route path="/games/slots" element={<ProtectedRoute><SlotsPage /></ProtectedRoute>} />
      <Route path="/games/mines" element={<ProtectedRoute><MinesPage /></ProtectedRoute>} />
      <Route path="/games/plinko" element={<ProtectedRoute><PlinkoPage /></ProtectedRoute>} />
      <Route path="/games/wheel" element={<ProtectedRoute><WheelPage /></ProtectedRoute>} />
      <Route path="/games/coinlemon" element={<ProtectedRoute><CoinLemonPage /></ProtectedRoute>} />
      <Route path="/games/crash" element={<ProtectedRoute><CrashPage /></ProtectedRoute>} />
      <Route path="/games/limbo" element={<ProtectedRoute><LimboPage /></ProtectedRoute>} />
      <Route path="/games/keno" element={<ProtectedRoute><KenoPage /></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><NewsBettingPage /></ProtectedRoute>} />
      <Route path="/ai-creator" element={<ProtectedRoute><AICreatorPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/vip" element={<ProtectedRoute><VIPPage /></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      <Route path="/tournaments" element={<ProtectedRoute><TournamentsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/promos" element={<ProtectedRoute><PromosPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatRoomPage /></ProtectedRoute>} />
      <Route path="/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
      <Route path="/responsible-gaming" element={<ProtectedRoute><ResponsibleGamingPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthContext>
          <NotificationProvider>
            <Toaster position="top-right" richColors />
            <AppRouter />
          </NotificationProvider>
        </AuthContext>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
